import React from 'react';
import { Table, Dropdown } from 'antd';
import { Group as GroupIcon } from 'lucide-react';
import PropTypes from 'prop-types';

function normalizeValue(value) {
    if (value == null) return '';
    if (Array.isArray(value)) return value.join(' ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function compareValues(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return normalizeValue(a).localeCompare(normalizeValue(b), 'ru', {
        numeric: true,
        sensitivity: 'base',
    });
}

function buildGroupedRows(rows, groupBy, groupByResolver) {
    if (!groupBy) {
        return [{ key: '__all__', label: null, rows }];
    }

    const groups = new Map();
    rows.forEach((row) => {
        const rawKey = groupByResolver
            ? groupByResolver(row, groupBy)
            : row?.[groupBy];
        const groupKey = rawKey == null || rawKey === '' ? 'Без группы' : normalizeValue(rawKey);
        if (!groups.has(groupKey)) {
            groups.set(groupKey, []);
        }
        groups.get(groupKey).push(row);
    });

    return Array.from(groups.entries()).map(([key, groupRows]) => ({
        key,
        label: key,
        rows: groupRows,
    }));
}

export const BaseTable = ({
    columns = [],
    data = [],
    className = '',
    variant = 'surface',
    onRowClick,
    searchQuery = '',
    searchKeys = [],
    sortBy,
    sortDir = 'asc',
    groupBy,
    groupByResolver,
    groupLabelResolver,
    showGroupCounts = true,
    pagination = false,
    pageSizeOptions = [6, 12, 18, 24],
    onChange,
    onSortChange,
    onGroupByChange,
}) => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const hasSearch = normalizedQuery.length > 0 && searchKeys.length > 0;

    const filteredRows = hasSearch
        ? data.filter((row) => searchKeys.some((key) => normalizeValue(row?.[key]).toLowerCase().includes(normalizedQuery)))
        : data;

    const sortedRows = sortBy
        ? [...filteredRows].sort((left, right) => {
            const dir = sortDir === 'desc' ? -1 : 1;
            return compareValues(left?.[sortBy], right?.[sortBy]) * dir;
        })
        : filteredRows;

    const grouped = buildGroupedRows(sortedRows, groupBy, groupByResolver).map((group) => ({
        ...group,
        label: groupLabelResolver ? groupLabelResolver(group.key, group.rows) : group.label,
    }));

    const showGroups = Boolean(groupBy);
    const isClickable = Boolean(onRowClick);

    const tableData = showGroups
        ? grouped.flatMap((group, groupIndex) => {
            const groupRow = {
                __rowKey: `__group-${group.key}-${groupIndex}`,
                __isGroupRow: true,
                __groupLabel: group.label || 'Без группы',
                __groupCount: group.rows.length,
            };

            const dataRows = group.rows.map((row, rowIdx) => ({
                ...row,
                __rowKey: row.id || `${group.key}-${rowIdx}`,
                __isGroupRow: false,
            }));

            return [groupRow, ...dataRows];
        })
        : grouped[0]?.rows.map((row, rowIdx) => ({
            ...row,
            __rowKey: row.id || `row-${rowIdx}`,
            __isGroupRow: false,
        })) || [];

    const antColumns = columns.map((col, colIdx) => {
        const canSort = Boolean(col.sortable && onSortChange);
        const canGroup = Boolean(col.groupable && onGroupByChange);
        const isGrouped = canGroup && groupBy === col.key;

        const groupMenuItems = isGrouped
            ? [{ key: 'clear', label: 'Убрать группировку' }]
            : [{ key: 'group', label: 'Группировать по этому полю' }];

        return {
        title: canGroup ? (
            <span className="inline-flex items-center gap-1">
                <span>{col.header}</span>
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: groupMenuItems,
                        onClick: ({ key, domEvent }) => {
                            domEvent?.stopPropagation();
                            onGroupByChange(key === 'clear' ? undefined : col.key);
                        },
                    }}
                >
                    <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className={`inline-flex ${isGrouped ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'}`}
                        title={isGrouped ? 'Группировка активна' : 'Группировать по этому полю'}
                    >
                        <GroupIcon size={13} />
                    </button>
                </Dropdown>
            </span>
        ) : col.header,
        dataIndex: col.key,
        key: col.key || String(colIdx),
        sorter: canSort ? true : undefined,
        sortOrder: canSort && sortBy === col.key ? (sortDir === 'desc' ? 'descend' : 'ascend') : undefined,
        showSorterTooltip: false,
        onCell: (row) => {
            if (!row.__isGroupRow) return {};
            if (colIdx === 0) return { colSpan: columns.length };
            return { colSpan: 0 };
        },
        render: (value, row) => {
            if (row.__isGroupRow) {
                if (colIdx !== 0) return null;
                return (
                    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
                        <span>{row.__groupLabel}</span>
                        {showGroupCounts && <span>{row.__groupCount}</span>}
                    </div>
                );
            }

            return col.render ? col.render(value, row) : value;
        },
        };
    });

    const tablePagination = pagination
        ? {
            showQuickJumper: false,
            showSizeChanger: true,
            pageSizeOptions,
            ...pagination,
        }
        : false;

    const handleTableChange = (paginationInfo, filters, sorter, extra) => {
        onChange?.(paginationInfo, filters, sorter, extra);

        if (onSortChange && sorter && !Array.isArray(sorter)) {
            const dir = sorter.order === 'descend' ? 'desc' : 'asc';
            onSortChange(sorter.order ? sorter.columnKey : undefined, dir);
        }
    };

    return (
        // <div className="w-full overflow-x-auto">
            <Table
                className={className}
                size="small"
                pagination={tablePagination}
                columns={antColumns}
                dataSource={tableData}
                rowKey="__rowKey"
                scroll={{ x: 'max-content' }}
                onChange={handleTableChange}
                rowClassName={(row) => {
                    if (row.__isGroupRow) return 'bg-zinc-100/70 dark:bg-zinc-900/40';
                    return isClickable ? 'hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors' : '';
                }}
                onRow={(row) => {
                    if (!isClickable || row.__isGroupRow) return {};

                    return {
                        onClick: () => onRowClick(row),
                        style: { cursor: 'pointer' },
                    };
                }}
            />
        // </div>
    );
};

BaseTable.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            header: PropTypes.node.isRequired,
            render: PropTypes.func, // Добавили валидацию для функции рендера
            sortable: PropTypes.bool,
            groupable: PropTypes.bool,
        })
    ).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    className: PropTypes.string,
    variant: PropTypes.string,
    onRowClick: PropTypes.func,
    searchQuery: PropTypes.string,
    searchKeys: PropTypes.arrayOf(PropTypes.string),
    sortBy: PropTypes.string,
    sortDir: PropTypes.oneOf(['asc', 'desc']),
    groupBy: PropTypes.string,
    groupByResolver: PropTypes.func,
    groupLabelResolver: PropTypes.func,
    showGroupCounts: PropTypes.bool,
    pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    pageSizeOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    onChange: PropTypes.func,
    onSortChange: PropTypes.func,
    onGroupByChange: PropTypes.func,
};

export const UiTable = Table;
export const UiTableHeader = ({ children }) => children ?? null;
export const UiTableRow = ({ children }) => children ?? null;
export const UiTableBody = ({ children }) => children ?? null;
export const UiTableCell = ({ children }) => children ?? null;
export const UiTableColumnHeader = ({ children }) => children ?? null;
export const UiTableRowHeader = ({ children }) => children ?? null;

UiTableHeader.propTypes = { children: PropTypes.node };
UiTableRow.propTypes = { children: PropTypes.node };
UiTableBody.propTypes = { children: PropTypes.node };
UiTableCell.propTypes = { children: PropTypes.node };
UiTableColumnHeader.propTypes = { children: PropTypes.node };
UiTableRowHeader.propTypes = { children: PropTypes.node };
